import React, { useEffect, useState, useRef, useCallback } from "react";
import { Route, Switch, Redirect, useLocation, NavLink } from "react-router-dom";
import {
  MsalAuthenticationTemplate,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import Home from "./components/Home";
import CopilotChat from "./copilot/CopilotChat";
import { loginRequest } from "./settings";
import { isInIframe } from "./components/iframe";
import "./copilot/Chat.css";
import EmailTriageLogs from "./components/EmailTriageLogs";
import UserRolesAdmin from "./components/UserRolesAdmin";
import BotConfigAdmin from "./components/BotConfigAdmin";
import { useTheme } from "./hooks/useTheme";
import { apiFetch } from "./auth/apiFetch";
import { devLog } from "./utils/devLog";
import { getAllowPopupsUrl } from "./utils/popupHelp";

const interactionType = isInIframe()
  ? InteractionType.Popup
  : InteractionType.Redirect;

/** Shown by MsalAuthenticationTemplate while the auth interaction is in progress. */
const AuthLoadingComponent = () => (
  <div className="text-center">
    <img src="/images/lnw-loader.gif" alt="Loading.." />
    <h2 className="text-center" style={{ color: "#fff" }}>
      Loading Chat ..!
    </h2>
  </div>
);

/**
 * Shown by MsalAuthenticationTemplate when authentication fails.
 * Detects popup-blocked errors and surfaces a targeted help card.
 */
const AuthErrorComponent = ({ error }) => {
  const isPopupBlocked =
    error?.errorCode === "popup_window_error" ||
    error?.errorCode === "empty_window_error";

  if (isPopupBlocked) {
    return (
      <div className="popup-blocked-card">
        <h3 className="popup-blocked-card__title">Security Popup Blocked</h3>
        <p className="popup-blocked-card__body">
          It looks like popups are blocked in your browser. Please allow popups
          for this site to continue.
        </p>
        <a
          className="popup-blocked-card__link"
          href={getAllowPopupsUrl()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Show me how to allow popups
        </a>
      </div>
    );
  }

  return (
    <div className="text-center">
      <h2 style={{ color: "#fff" }}>
        Authentication failed. Please refresh the page.
      </h2>
    </div>
  );
};

/**
 * App � root component.
 *
 * Wraps all routes in MsalAuthenticationTemplate so every page requires a valid
 * Azure AD session. On mount it fetches the signed-in user's Graph profile and
 * profile photo, storing them on window globals so CopilotChat can access them
 * inside WebChat's Redux store middleware (which runs outside React's render cycle).
 *
 * Routes:
 *   /                  � PeopleHub chat (or login page if unauthenticated)
 *   /chat/peoplehub    � PeopleHub HR assistant
 *   /chat/goldie       � Goldie general-purpose assistant
 *   /chat/ITSD         � ITSD IT service desk assistant
 *   /login/callback    � MSAL redirect landing (handled by MSAL; no component needed)
 */
function App() {
  const location = useLocation();
  const [msalUser, setMsalUser] = useState(null);
  const [userRole, setUserRole] = useState(undefined);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [botConfigs, setBotConfigs] = useState([]);
  const [botConfigsLoaded, setBotConfigsLoaded] = useState(false);
  const containerRef = useRef(null);
  const skipScrollRestoreRef = useRef(false);
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { theme, toggle: toggleTheme } = useTheme();
  const isInIframe = window.self !== window.top;

  devLog("Inside AppContent");
  devLog("B4 MSAL :");
  devLog("A4 MSAL :", msalUser);

  /**
   * MSAL can populate `accounts` (and flip isAuthenticated to true) on a render
   * that runs before its own internal setActiveAccount() call has landed — see
   * msal-react's hooks.md ("getActiveAccount() may return null immediately after
   * authentication completes"). Every apiFetch call relies on getActiveAccount(),
   * so without this, API calls made right after a fresh login can throw "No
   * signed-in account available" even though the user is in fact signed in.
   */
  useEffect(() => {
    if (accounts.length > 0 && !instance.getActiveAccount()) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  /** Scrolls the chat container to the bottom � passed as onSendboxClick to CopilotChat. */
  const scrollToBottom = useCallback(() => {
    devLog("scroll To Bottom");
    skipScrollRestoreRef.current = true;
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, []);

  /**
   * Fetches the signed-in user's Microsoft Graph profile and profile photo.
   * Stores results on window.__MSAL_USER__ and window.__MSAL_USER_PIC__ so the
   * WebChat store middleware (outside React context) can access them when enriching
   * outgoing activity payloads.
   */
  const userAccountFetch = async () => {
    let mounted = true;
    let objectUrl = null;
    devLog("Inside user Account fetch");
    const account = instance.getActiveAccount() || accounts[0];
    const active = account;
    if (!active && instance.getAllAccounts().length > 0) {
      devLog("Inside Not Active Account If");
      instance.setActiveAccount(instance.getAllAccounts()[0]);
    }
    if (!account) return;

    if (account) {
      devLog("Inside Active Account If");
      try {
        let token = null;
        try {
          devLog("Inside userAccountFetch try : ");
          token = await instance.acquireTokenSilent({
            account: accounts[0],
            scopes: ["User.Read", "User.Read.All"],
          });
        } catch (e) {
          if (isInIframe()) {
            devLog("Inside userAccountFetch catch-If : ", isInIframe());
            token = await instance.acquireTokenPopup({
              account: accounts[0],
              scopes: ["User.Read", "User.Read.All"],
            });
            return;
          } else {
            devLog("Inside userAccountFetch catch-Else : ", isInIframe());
            token = await instance.acquireTokenRedirect({
              account: accounts[0],
              scopes: ["User.Read", "User.Read.All"],
            });
          }
        }

        const graphURL = `https://graph.microsoft.com/v1.0/users?$filter=accountEnabled eq true and mail ne null and endsWith(mail,'${accounts[0].username}')&$select=displayName,givenName,mail,city,country,userPrincipalName`;
        const graphResp = await fetch(graphURL.toString(), {
          headers: {
            Authorization: `Bearer ${token.accessToken}`,
            ConsistencyLevel: `eventual`,
          },
        });

        if (!graphResp.ok) {
          devLog("Inside Graph resp not okay");
          const errText = await graphResp.text().catch(() => "<no body>");
          throw new Error(
            `Graph fetch failed ${graphResp.status} ${graphResp.statusText} � ${errText}`,
          );
        }
        const profile = await graphResp.json();
        devLog("Profile :", profile);

        setMsalUser((prev) =>
          prev?.mail === profile.value[0]?.mail ? prev : profile.value[0],
        );
        devLog("MSAL User setup");
        window.__MSAL_USER__ = profile.value[0];

        try {
          // The backend derives identity from the verified token, not this query
          // string � it's kept only as a hint for logs, never trusted server-side.
          const roleResp = await apiFetch("/api/user-role", {}, instance, active);
          if (roleResp.ok) {
            const roleData = await roleResp.json();
            setUserRole(roleData.role ?? null);
          } else {
            setUserRole(null);
          }
        } catch (roleErr) {
          console.warn("Could not fetch user role:", roleErr);
          setUserRole(null);
        }

        try {
          const profileURL = `https://graph.microsoft.com/v1.0/users/${accounts[0].localAccountId}/photo/$value`;
          devLog("URL : ", profileURL.toString());
          const profileResp = await fetch(profileURL.toString(), {
            headers: { Authorization: `Bearer ${token.accessToken}` },
          });
          if (!profileResp.ok) {
            console.warn(
              "profilePic fetch failed",
              profileResp.status,
              profileResp.statusText,
            );
          } else {
            const profilePicBlob = await profileResp.blob();
            objectUrl = URL.createObjectURL(profilePicBlob);
            if (mounted) {
              window.__MSAL_USER_PIC__ = objectUrl;
              devLog("Profile Pic URL Set : " + objectUrl);
            }
          }
        } catch (photoErr) {
          console.warn("Error fetching profile photo:", photoErr);
        }
      } catch (e) {
        console.error("Failed to load Graph profile:", e);
      }
    }
  };

  /** Triggers the MSAL logout redirect flow. */
  const handleLogout = async () => {
    instance.logoutRedirect();
  };

  /** Fetches the user profile once MSAL auth is settled and the user is authenticated. */
  useEffect(() => {
    if (inProgress !== "none" || !isAuthenticated) return;
    devLog("Inside L1");
    const account = instance.getActiveAccount() || accounts?.[0];
    devLog("Inside L2");
    if (!account) return;
    devLog("Inside L3");
    (async () => {
      await userAccountFetch();
    })();
    devLog("Inside L4");
  }, [inProgress, isAuthenticated, instance, accounts]);

  const loadBotConfigs = useCallback(() => {
    apiFetch("/api/bot-configs", {}, instance)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((j) => { setBotConfigs(j.data || []); setBotConfigsLoaded(true); })
      .catch((e) => { console.warn("Could not load bot configs:", e); setBotConfigsLoaded(true); });
  }, [instance]);

  // Bot configs require a signed-in account (the endpoint now checks the caller's
  // token), so wait until MSAL has settled and authentication has completed.
  useEffect(() => {
    if (inProgress !== "none" || !isAuthenticated) return;
    loadBotConfigs();
  }, [inProgress, isAuthenticated, loadBotConfigs]);

  const defaultBot = botConfigs.find((b) => b.IsActive);
  const noActiveBots = !defaultBot;

  return (
    <MsalAuthenticationTemplate
      interactionType={interactionType}
      authenticationRequest={loginRequest}
      loadingComponent={AuthLoadingComponent}
      errorComponent={AuthErrorComponent}
    >
      <section className="container" ref={containerRef}>
        <nav className={navCollapsed ? "nav-collapsed" : ""}>
          {/* <div className="nav-header">
            <img
              src="/images/lnw-logo-black.png"
              alt="LNW Logo"
              className="nav-logo"
            />
            <div
              className="cursor-pointer"
              onClick={() => setNavCollapsed((c) => !c)}
            > */}
          <div className="nav-header">
            <img
              src="/images/lnw-logo-black.png"
              alt="LNW Logo"
              className="nav-logo"
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {/* User avatar top-right */}
              {msalUser && (
                <div className="nav-topright-profile" title={`${msalUser.displayName}${userRole ? ` (${userRole})` : ""}\n${msalUser.mail}`}>       <div className="nav-profile__avatar">
                  {window.__MSAL_USER_PIC__ ? (
                    <img src={window.__MSAL_USER_PIC__} alt={msalUser.displayName} className="nav-profile__img" />
                  ) : (
                    <span className="nav-profile__initials">
                      {msalUser.displayName?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </span>
                  )}
                </div>
                </div>
              )}
              <svg
              onClick={() => setNavCollapsed((c) => !c)}
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                data-rtl-flip=""
                className="icon max-md:hidden nav-collapse-icon cursor-pointer"
              >
                <path d="M6.83496 3.99992C6.38353 4.00411 6.01421 4.0122 5.69824 4.03801C5.31232 4.06954 5.03904 4.12266 4.82227 4.20012L4.62207 4.28606C4.18264 4.50996 3.81498 4.85035 3.55859 5.26848L3.45605 5.45207C3.33013 5.69922 3.25006 6.01354 3.20801 6.52824C3.16533 7.05065 3.16504 7.71885 3.16504 8.66301V11.3271C3.16504 12.2712 3.16533 12.9394 3.20801 13.4618C3.25006 13.9766 3.33013 14.2909 3.45605 14.538L3.55859 14.7216C3.81498 15.1397 4.18266 15.4801 4.62207 15.704L4.82227 15.79C5.03904 15.8674 5.31234 15.9205 5.69824 15.9521C6.01398 15.9779 6.383 15.986 6.83398 15.9902L6.83496 3.99992ZM18.165 11.3271C18.165 12.2493 18.1653 12.9811 18.1172 13.5702C18.0745 14.0924 17.9916 14.5472 17.8125 14.9648L17.7295 15.1415C17.394 15.8 16.8834 16.3511 16.2568 16.7353L15.9814 16.8896C15.5157 17.1268 15.0069 17.2285 14.4102 17.2773C13.821 17.3254 13.0893 17.3251 12.167 17.3251H7.83301C6.91071 17.3251 6.17898 17.3254 5.58984 17.2773C5.06757 17.2346 4.61294 17.1508 4.19531 16.9716L4.01855 16.8896C3.36014 16.5541 2.80898 16.0434 2.4248 15.4169L2.27051 15.1415C2.03328 14.6758 1.93158 14.167 1.88281 13.5702C1.83468 12.9811 1.83496 12.2493 1.83496 11.3271V8.66301C1.83496 7.74072 1.83468 7.00898 1.88281 6.41985C1.93157 5.82309 2.03329 5.31432 2.27051 4.84856L2.4248 4.57317C2.80898 3.94666 3.36012 3.436 4.01855 3.10051L4.19531 3.0175C4.61285 2.83843 5.06771 2.75548 5.58984 2.71281C6.17898 2.66468 6.91071 2.66496 7.83301 2.66496H12.167C13.0893 2.66496 13.821 2.66468 14.4102 2.71281C15.0069 2.76157 15.5157 2.86329 15.9814 3.10051L16.2568 3.25481C16.8833 3.63898 17.394 4.19012 17.7295 4.84856L17.8125 5.02531C17.9916 5.44285 18.0745 5.89771 18.1172 6.41985C18.1653 7.00898 18.165 7.74072 18.165 8.66301V11.3271ZM8.16406 15.995H12.167C13.1112 15.995 13.7794 15.9947 14.3018 15.9521C14.8164 15.91 15.1308 15.8299 15.3779 15.704L15.5615 15.6015C15.9797 15.3451 16.32 14.9774 16.5439 14.538L16.6299 14.3378C16.7074 14.121 16.7605 13.8478 16.792 13.4618C16.8347 12.9394 16.835 12.2712 16.835 11.3271V8.66301C16.835 7.71885 16.8347 7.05065 16.792 6.52824C16.7605 6.14232 16.7073 5.86904 16.6299 5.65227L16.5439 5.45207C16.32 5.01264 15.9796 4.64498 15.5615 4.3886L15.3779 4.28606C15.1308 4.16013 14.8165 4.08006 14.3018 4.03801C13.7794 3.99533 13.1112 3.99504 12.167 3.99504H8.16406C8.16407 3.99667 8.16504 3.99829 8.16504 3.99992L8.16406 15.995Z" />
              </svg>
            </div>
          </div>
          <div className="nav-heading" hidden={noActiveBots}>AI Chatbot</div>
          <ul className="navlist">
            {botConfigs.filter((b) => b.IsActive).map((bot) => (
              <li key={bot.Id} className={location.pathname === bot.RoutePath ? "active" : ""}>
                <NavLink
                  to={bot.RoutePath}
                  isActive={(match, loc) => loc.pathname === bot.RoutePath}
                >
                  <img src={bot.NavIcon || "/images/robot.png"} width="30" alt={bot.NavLabel} />
                  <span>{bot.NavLabel}</span>
                </NavLink>
              </li>
            ))}
            {/* �"��"� Monitoring (Admin + Viewer) �"��"� */}
            {(userRole === "System Admin" || userRole === "Email Triage Viewer") && (
              <>
                <li className="nav-heading">Management</li>
                <li className={location.pathname === "/chat/email-triage" ? "active" : ""
                  }
                >
                  <NavLink
                    to="/chat/email-triage"
                    isActive={(match, loc) => loc.pathname === "/chat/email-triage"}
                  >
                    <img
                      src="/images/itsd-email-support.png"
                      width="30"
                      alt="Email Triage Logs"
                    />
                    <span>Email Triage Logs</span>
                  </NavLink>
                </li>
              </>
            )}
            {/* �"��"� Admin Panel (Admin only) �"��"� */}
            {userRole === "System Admin" && (
              <>
                <li className="nav-heading">Administration</li>
                <li className={location.pathname === "/admin/user-roles" ? "active" : ""}>
                  <NavLink
                    to="/admin/user-roles"
                    isActive={(match, loc) => loc.pathname === "/admin/user-roles"}
                  >
                    <img src="/images/defaultuser.png" width="30" alt="User Role Management" />
                    <span>User Role Management</span>
                  </NavLink>
                </li>
                <li className={location.pathname === "/admin/bot-config" ? "active" : ""}>
                  <NavLink
                    to="/admin/bot-config"
                    isActive={(match, loc) => loc.pathname === "/admin/bot-config"}
                  >
                    <img src="/images/agent-config1.png" width="30" alt="Agent Configuration" />
                    <span>Agent Configuration</span>
                  </NavLink>
                </li>
              </>
            )}
          </ul>
          <div
            className="logout-btn1"
            aria-label="Logout"
            title="Logout"
            onClick={handleLogout}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-box-arrow-in-right logout-icon"
              viewBox="0 0 18 18"
            >
              <path
                fillRule="evenodd"
                d="M6 3.5a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-2a.5.5 0 0 0-1 0v2A1.5 1.5 0 0 0 6.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2h-8A1.5 1.5 0 0 0 5 3.5v2a.5.5 0 0 0 1 0z"
              />
              <path
                fillRule="evenodd"
                d="M11.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 1 0-.708.708L10.293 7.5H1.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708z"
              />
            </svg>
            <span>Logout</span>
          </div>
        </nav>
        <main>
          <div className="main-topbar">
            {/* {!isInIframe && !(/\/(peoplehub|goldie)/.test(location.pathname) || (location.pathname === '/' && (!defaultBot || /peoplehub|goldie/i.test(defaultBot.AgentKey)))) && <button */}
            {!isInIframe && <button
              type="button"
              className="theme-toggle"
              onClick={() => {
                const scrollTop = containerRef.current?.scrollTop ?? 0;
                skipScrollRestoreRef.current = false;
                toggleTheme();
                requestAnimationFrame(() => {
                  if (!skipScrollRestoreRef.current && containerRef.current) {
                    containerRef.current.scrollTop = scrollTop;
                  }
                  skipScrollRestoreRef.current = false;
                });
              }}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.552 18.894a.75.75 0 001.06 1.06l1.591-1.59a.75.75 0 00-1.06-1.061l-1.591 1.59zM3 12a.75.75 0 01.75-.75H6a.75.75 0 010 1.5H3.75A.75.75 0 013 12zM6.166 5.106a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591z" />
                </svg>
              ) : (
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
                </svg>
              )}
            </button>}
          </div>
          <section className="main-inner">
            <Switch>
              <Route path="/" exact>
                {isAuthenticated ? (
                  msalUser ? (
                    !botConfigsLoaded ? (
                      <div className="text-center">
                        <img src="/images/lnw-loader.gif" alt="Loading.." />
                        <h2 className="text-center">Loading Chat ..!</h2>
                      </div>
                    ) : defaultBot ? (
                      <CopilotChat
                        key="root"
                        agentKey={defaultBot.AgentKey}
                        botConfig={defaultBot}
                        msalUser={msalUser}
                        onSendboxClick={scrollToBottom}
                        theme={theme}
                      />
                    ) : (
                      <div className="text-center">
                        <h2 className="text-center">No active bot configured.</h2>
                        <p>Please enable a bot in the admin panel.</p>
                      </div>
                    )
                  ) : (
                    <div className="text-center">
                      <img src="/images/lnw-loader.gif" alt="Loading.." />
                      <h2 className="text-center">
                        User Session not found ..!
                      </h2>
                    </div>
                  )
                ) : (
                  <Home />
                )}
              </Route>
              {botConfigs.map((bot) => (
                <Route key={bot.AgentKey} path={bot.RoutePath} exact>
                  {bot.IsActive ? (
                    <CopilotChat
                      key={bot.AgentKey}
                      agentKey={bot.AgentKey}
                      botConfig={bot}
                      msalUser={msalUser}
                      onSendboxClick={scrollToBottom}
                      theme={theme}
                    />
                  ) : (
                    <div className="text-center">
                      <h2 className="text-center">Bot Unavailable</h2>
                      <p>This bot is currently disabled. Please contact your administrator.</p>
                    </div>
                  )}
                </Route>
              ))}
              <Route path="/admin/user-roles" exact>
                {userRole === undefined ? (
                  <div className="text-center">
                    <img src="/images/lnw-loader.gif" alt="Loading.." />
                  </div>
                ) : userRole === "System Admin" ? (
                  <div className="email-triage-page">
                    <UserRolesAdmin />
                  </div>
                ) : (
                  <Redirect to="/" />
                )}
              </Route>
              <Route path="/chat/email-triage" exact>
                {userRole === undefined ? (
                  <div className="text-center">
                    <img src="/images/lnw-loader.gif" alt="Loading.." />
                  </div>
                ) : (userRole === "System Admin" || userRole === "Email Triage Viewer") ? (
                  <div className="email-triage-page">
                    <EmailTriageLogs />
                  </div>
                ) : (
                  <Redirect to="/" />
                )}
              </Route>
              <Route path="/admin/bot-config" exact>
                {userRole === undefined ? (
                  <div className="text-center">
                    <img src="/images/lnw-loader.gif" alt="Loading.." />
                  </div>
                ) : userRole === "System Admin" ? (
                  <div className="email-triage-page">
                    <BotConfigAdmin onConfigChange={loadBotConfigs} />
                  </div>
                ) : (
                  <Redirect to="/" />
                )}
              </Route>
              <Route path="/login/callback" />
            </Switch>
          </section>
        </main>
      </section>
    </MsalAuthenticationTemplate>
  );
}

export default App;
